@if (currentSession(); as session) {
  <div class="drawer-header">
    <div class="header-title">
      <img src="assets/images/logo/m365Copilot.svg" alt="" class="header-icon" />
      <h2>{{ getAssessmentName(session) }}</h2>
    </div>
  </div>

  <div class="drawer-metadata">
    <div class="meta-item meta-status">
      <span class="meta-label">
        {{ 'attackSimulationsPage.drawer.statusLabel' | labelsProvider }}
      </span>
      <app-simulation-status-badge [status]="session.status" />
    </div>
    <div class="meta-item meta-ai-service">
      <span class="meta-label">
        {{ 'attackSimulationsPage.drawer.aiServiceLabel' | labelsProvider }}
      </span>
      <span class="meta-value ai-service">
        <img src="assets/images/logo/m365Copilot.svg" alt="" class="meta-icon" />
        {{ getAiServiceDisplay(session.aiService) }}
      </span>
    </div>
    <div class="meta-item">
      <span class="meta-label">
        {{ 'attackSimulationsPage.drawer.attackStrategyLabel' | labelsProvider }}
      </span>
      <span class="meta-value">
        {{ formatAttackStrategy(session.attackStrategy) }}
      </span>
    </div>
    <div class="meta-item meta-test-user">
      <span class="meta-label">
        {{ 'attackSimulationsPage.drawer.testUserLabel' | labelsProvider }}
      </span>
      <span class="meta-value">
        {{ session.onBehalfOfIdentity }}
      </span>
    </div>
  </div>

  <div class="drawer-body">
    @if (isInProgress()) {
      <div class="progress-section">
        <h3 class="progress-title">
          {{ 'attackSimulationsPage.drawer.inProgressTitle' | labelsProvider }}
        </h3>
        <p class="progress-subtitle">
          {{ 'attackSimulationsPage.drawer.inProgressSubtitle' | labelsProvider }}
        </p>

        <div class="steps-card">
          @for (step of steps(); track step.label) {
            <div
              class="step-item"
              [class.completed]="step.state === 'completed'"
              [class.active]="step.state === 'active'"
              [class.pending]="step.state === 'pending'"
            >
              <div class="step-icon">
                @if (step.state === 'completed') {
                  <lucide-icon name="check-circle-2" [size]="18" />
                } @else if (step.state === 'active') {
                  <mat-progress-spinner color="primary" mode="indeterminate" [diameter]="18" />
                } @else {
                  <lucide-icon name="circle" [size]="18" />
                }
              </div>
              <span class="step-label">
                {{ step.label | labelsProvider }}
              </span>
            </div>
          }
        </div>

        <div class="safety-notice">
          <lucide-icon name="lock" [size]="16" />
          <div>
            <span>
              {{ 'attackSimulationsPage.drawer.safetyNotice' | labelsProvider }}
            </span>
            <br />
            <span class="safety-detail">
              {{ 'attackSimulationsPage.drawer.safetyNoticeDetail' | labelsProvider }}
            </span>
          </div>
        </div>
      </div>
    }

    @if (isFinished()) {
      <div class="results-section">
        <div class="results-summary">
          <div class="results-count-text">
            <span class="result-count">{{ issueCount() }}</span>
            {{ issueCount() === 1 ? 'finding' : 'findings' }}
          </div>
          @if (issueCount() > 0) {
            <button class="export-csv-btn" (click)="onExportCsv()">
              <lucide-icon name="download" [size]="14" />
              {{ 'attackSimulationsPage.drawer.exportToCsv' | labelsProvider }}
            </button>
          }
        </div>

        @if (issueCount() > 0) {
          <div class="files-table">
            <div class="files-table-header">
              <button class="sort-header col-filename" (click)="toggleSort('fileName')">
                {{ 'attackSimulationsPage.drawer.colFileName' | labelsProvider }}
                <lucide-icon
                  [name]="sortField() === 'fileName' && sortDirection() === 'desc' ? 'chevron-down' : 'chevron-up'"
                  [size]="14"
                  class="sort-icon"
                  [class.active]="sortField() === 'fileName'"
                />
              </button>
              <button class="sort-header col-sensitivity" (click)="toggleSort('sensitivity')">
                {{ 'attackSimulationsPage.drawer.colSensitivity' | labelsProvider }}
                <lucide-icon
                  [name]="sortField() === 'sensitivity' && sortDirection() === 'desc' ? 'chevron-down' : 'chevron-up'"
                  [size]="14"
                  class="sort-icon"
                  [class.active]="sortField() === 'sensitivity'"
                />
              </button>
              <span class="sort-header col-location">
                {{ 'attackSimulationsPage.drawer.colLocation' | labelsProvider }}
              </span>
              <span class="col-expand"></span>
            </div>
            @for (issue of sortedIssues(); track $index) {
              <div class="file-row" [class.expanded]="expandedIssueIdx() === $index">
                <button class="file-row-main" (click)="toggleIssue($index)">
                  <span class="col-filename">
                    @if (getFileTypeIcon(issue)) {
                      <img [src]="getFileTypeIcon(issue)" alt="" class="file-type-icon" />
                    } @else {
                      <lucide-icon name="file-text" [size]="14" />
                    }
                    {{ getFileName(issue) }}
                  </span>
                  <span class="col-sensitivity">
                    <span
                      class="sensitivity-icon-container"
                      [style.background-color]="getSensitivityConfig(issue).bgColor"
                      [style.color]="getSensitivityConfig(issue).iconColor"
                    >
                      <lucide-icon [name]="getSensitivityConfig(issue).icon" [size]="14" />
                    </span>
                    {{ getSensitivityConfig(issue).label }}
                  </span>
                  <span class="col-location" [title]="issue.fileUrl || ''">
                    {{ getShortenedUrl(issue) }}
                  </span>
                  <span class="col-expand">
                    <lucide-icon
                      name="chevron-down"
                      [size]="16"
                      class="expand-icon"
                      [class.rotated]="expandedIssueIdx() === $index"
                    />
                  </span>
                </button>

                @if (expandedIssueIdx() === $index) {
                  <div class="file-details">
                    <div class="detail-row">
                      <span class="detail-label">
                        {{ 'attackSimulationsPage.drawer.location' | labelsProvider }}
                      </span>
                      <a [href]="issue.fileUrl" target="_blank" class="detail-link">
                        {{ issue.fileUrl || '-' }}
                      </a>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">
                        {{ 'attackSimulationsPage.drawer.description' | labelsProvider }}
                      </span>
                      <span>{{ issue.description || '-' }}</span>
                    </div>
                    @if (issue.evidenceText) {
                      <div class="detail-row">
                        <span class="detail-label">
                          {{ 'attackSimulationsPage.drawer.evidence' | labelsProvider }}
                        </span>
                        <span>{{ issue.evidenceText }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        } @else if (isFailed()) {
          <div class="no-findings">
            <p class="no-findings-title">
              {{ 'attackSimulationsPage.drawer.assessmentFailed' | labelsProvider }}
            </p>
            <p class="no-findings-message">
              {{ 'attackSimulationsPage.drawer.assessmentFailedMessage' | labelsProvider }}
            </p>
          </div>
        } @else {
          <div class="no-findings">
            <p class="no-findings-title">
              {{ 'attackSimulationsPage.drawer.noFindings' | labelsProvider }}
            </p>
            <p class="no-findings-message">
              {{ 'attackSimulationsPage.drawer.noFindingsMessage' | labelsProvider }}
            </p>
          </div>
        }
      </div>
    }
  </div>
}
